import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useLang } from "@/i18n/LangContext";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  UserPlus, Link2, Settings, Users, MessageSquare, Star, Trash2,
  Image, Download, Upload, History, TicketCheck, X, Save, Search, Lock, Mail,
  ArrowUpDown, ArrowUp, ArrowDown, Shield, FileDown, AlertTriangle, EyeOff,
  LayoutDashboard, TrendingUp, ChevronLeft, ChevronRight, Filter,
  Palette, UserCog, ArrowRightLeft, QrCode, Camera, Check, Calendar,
} from "lucide-react";
import AppointmentsTab from "@/components/AppointmentsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Certificate Generator ──────────────────────────────

function generateCertificate(clientData) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = 1240;
    canvas.height = 1754;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const grd = ctx.createRadialGradient(620, 700, 100, 620, 700, 600);
    grd.addColorStop(0, "rgba(217, 70, 239, 0.08)");
    grd.addColorStop(1, "rgba(5, 5, 5, 0)");
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = "rgba(217, 70, 239, 0.3)";
    ctx.lineWidth = 2;
    ctx.strokeRect(60, 60, canvas.width - 120, canvas.height - 120);
    ctx.strokeStyle = "rgba(217, 70, 239, 0.1)";
    ctx.lineWidth = 1;
    ctx.strokeRect(80, 80, canvas.width - 160, canvas.height - 160);

    ctx.strokeStyle = "rgba(217, 70, 239, 0.6)";
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(60, 100); ctx.lineTo(60, 60); ctx.lineTo(100, 60); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(canvas.width - 100, 60); ctx.lineTo(canvas.width - 60, 60); ctx.lineTo(canvas.width - 60, 100); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(60, canvas.height - 100); ctx.lineTo(60, canvas.height - 60); ctx.lineTo(100, canvas.height - 60); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(canvas.width - 100, canvas.height - 60); ctx.lineTo(canvas.width - 60, canvas.height - 60); ctx.lineTo(canvas.width - 60, canvas.height - 100); ctx.stroke();

    ctx.strokeStyle = "rgba(217, 70, 239, 0.4)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(200, 200); ctx.lineTo(canvas.width - 200, 200); ctx.stroke();

    ctx.fillStyle = "#d946ef";
    ctx.font = "bold 28px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText("P U R P L E   I N K", canvas.width / 2, 170);

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "14px Georgia, serif";
    ctx.fillText("T A T T O O   S T U D I O", canvas.width / 2, 240);

    ctx.fillStyle = "rgba(217, 70, 239, 0.5)";
    ctx.beginPath();
    ctx.moveTo(canvas.width / 2, 290); ctx.lineTo(canvas.width / 2 + 8, 298);
    ctx.lineTo(canvas.width / 2, 306); ctx.lineTo(canvas.width / 2 - 8, 298);
    ctx.closePath(); ctx.fill();

    ctx.fillStyle = "#e5e5e5";
    ctx.font = "italic 22px Georgia, serif";
    ctx.fillText("Certificate of Authenticity", canvas.width / 2, 400);

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 52px Georgia, serif";
    ctx.fillText("WELCOME", canvas.width / 2, 510);

    ctx.fillStyle = "#d946ef";
    ctx.font = "bold 44px Georgia, serif";
    ctx.fillText(clientData.name.toUpperCase(), canvas.width / 2, 590);

    ctx.strokeStyle = "rgba(217, 70, 239, 0.3)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(350, 640); ctx.lineTo(canvas.width - 350, 640); ctx.stroke();

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "16px Georgia, serif";
    ctx.fillText("You are now a valued member of our exclusive client circle.", canvas.width / 2, 700);
    ctx.fillText("Your personal credentials for the client portal:", canvas.width / 2, 730);

    ctx.fillStyle = "rgba(217, 70, 239, 0.05)";
    ctx.strokeStyle = "rgba(217, 70, 239, 0.2)";
    ctx.lineWidth = 1;
    ctx.fillRect(220, 780, 800, 280);
    ctx.strokeRect(220, 780, 800, 280);

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "13px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText("U S E R N A M E", canvas.width / 2, 840);

    ctx.fillStyle = "#e5e5e5";
    ctx.font = "bold 28px Courier New, monospace";
    ctx.fillText(clientData.username, canvas.width / 2, 880);

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "13px Georgia, serif";
    ctx.fillText("P A S S W O R D", canvas.width / 2, 950);

    ctx.fillStyle = "#d946ef";
    ctx.font = "bold 36px Courier New, monospace";
    ctx.fillText(clientData.plain_password, canvas.width / 2, 1000);

    ctx.strokeStyle = "rgba(217, 70, 239, 0.3)";
    ctx.beginPath(); ctx.moveTo(350, 1120); ctx.lineTo(canvas.width - 350, 1120); ctx.stroke();

    ctx.fillStyle = "#e5e5e5";
    ctx.font = "18px Georgia, serif";
    ctx.fillText("Your starting discount: " + clientData.discount + "%", canvas.width / 2, 1180);

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "15px Georgia, serif";
    ctx.fillText("Refer friends to earn up to 65% discount.", canvas.width / 2, 1220);
    ctx.fillText("Your referral code: " + clientData.referral_code, canvas.width / 2, 1250);

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "14px Georgia, serif";
    ctx.fillText("Issued: " + clientData.created_date, canvas.width / 2, 1340);

    ctx.strokeStyle = "rgba(217, 70, 239, 0.4)";
    ctx.beginPath(); ctx.moveTo(200, 1550); ctx.lineTo(canvas.width - 200, 1550); ctx.stroke();

    ctx.fillStyle = "#666666";
    ctx.font = "12px Georgia, serif";
    ctx.fillText("This certificate is your key to the Purple INK client portal.", canvas.width / 2, 1600);
    ctx.fillText("Please keep your credentials safe and do not share them.", canvas.width / 2, 1625);

    ctx.fillStyle = "rgba(217, 70, 239, 0.4)";
    ctx.font = "bold 16px Georgia, serif";
    ctx.fillText("PURPLE INK", canvas.width / 2, 1680);

    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95);
  });
}

// ── Admin Page ─────────────────────────────────────────

export default function AdminPage() {
  const { token, user: authUser } = useAuth();
  const { t } = useLang();
  const headers = { Authorization: `Bearer ${token}` };
  const isMaster = authUser?.role === "master_admin";

  const [clients, setClients] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);

  // Create client form
  const [newClient, setNewClient] = useState({ name: "", email: "", initial_discount: 5 });
  const [creating, setCreating] = useState(false);
  const [createdClient, setCreatedClient] = useState(null);

  // Referral form
  const [refCode, setRefCode] = useState("");
  const [refName, setRefName] = useState("");
  const [addingRef, setAddingRef] = useState(false);

  // Gallery upload
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("general");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Email certificate
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);

  // History dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Delete client (double confirm)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0); // 0=none, 1=first confirm, 2=second confirm

  // Sort
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // Audit log
  const [auditLogs, setAuditLogs] = useState([]);

  // Export
  const [exporting, setExporting] = useState(false);

  // Dashboard stats
  const [stats, setStats] = useState(null);

  // Pagination
  const [clientsPage, setClientsPage] = useState(1);
  const [auditPage, setAuditPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  // Audit filters
  const [auditActionFilter, setAuditActionFilter] = useState("");
  const [auditActorFilter, setAuditActorFilter] = useState("");

  // Settings form
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  // Artists management (master admin only)
  const [artists, setArtists] = useState([]);
  const [newArtist, setNewArtist] = useState({ name: "", password: "" });
  const [creatingArtist, setCreatingArtist] = useState(false);
  const [artistFilter, setArtistFilter] = useState("");

  // Reassign client
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignArtistId, setReassignArtistId] = useState("");

  // Artist performance report
  const [artistPerformance, setArtistPerformance] = useState([]);

  // Reset artist password
  const [resetPwTarget, setResetPwTarget] = useState(null);
  const [resetPwValue, setResetPwValue] = useState("");

  // Dashboard artist filter (master admin)
  const [dashboardArtistFilter, setDashboardArtistFilter] = useState("");

  // QR Scanner
  const [scannedClient, setScannedClient] = useState(null);
  const [scannerActive, setScannerActive] = useState(false);
  const [scanError, setScanError] = useState("");
  const [claimingQr, setClaimingQr] = useState(false);
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  // Memoised sorted discount distribution rows (avoid sort/entries/map each render)
  const discountDistRows = useMemo(() => {
    if (!stats?.discount_distribution) return [];
    const total = stats.total_clients || 0;
    return Object.entries(stats.discount_distribution)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([disc, count]) => ({
        disc,
        count,
        pct: total > 0 ? (count / total) * 100 : 0,
      }));
  }, [stats]);

  // Helper: render the sort indicator icon (avoids nested ternary in JSX)
  const renderSortIcon = (key) => {
    if (sortKey !== key) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const fetchClients = useCallback(() => {
    axios.get(`${API}/admin/clients`, { headers }).then((res) => setClients(res.data)).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSettings = useCallback(() => {
    axios.get(`${API}/admin/settings`, { headers }).then((res) => {
      setSettings(res.data);
      setSettingsForm(res.data);
    }).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReviews = useCallback(() => {
    axios.get(`${API}/reviews`).then((res) => setReviews(res.data.reviews || [])).catch(() => {});
  }, []);

  const fetchContacts = useCallback(() => {
    axios.get(`${API}/admin/contacts`, { headers }).then((res) => setContacts(res.data)).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGallery = useCallback(() => {
    axios.get(`${API}/gallery`).then((res) => setGalleryItems(res.data)).catch(() => {});
  }, []);

  const fetchAuditLogs = useCallback(() => {
    axios.get(`${API}/admin/audit-logs`, { headers }).then((res) => setAuditLogs(res.data)).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = useCallback((filterArtistId) => {
    const params = filterArtistId ? `?artist_id=${filterArtistId}` : "";
    axios.get(`${API}/admin/stats${params}`, { headers }).then((res) => setStats(res.data)).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArtists = useCallback(() => {
    if (isMaster) {
      axios.get(`${API}/admin/artists`, { headers }).then((res) => setArtists(res.data)).catch(() => {});
    }
  }, [token, isMaster]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArtistPerformance = useCallback(() => {
    if (isMaster) {
      axios.get(`${API}/admin/artist-performance`, { headers }).then((res) => setArtistPerformance(res.data)).catch(() => {});
    }
  }, [token, isMaster]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchClients();
    fetchStats();
    fetchArtists();
    fetchArtistPerformance();
    if (isMaster) {
      fetchSettings();
      fetchReviews();
      fetchContacts();
      fetchGallery();
      fetchAuditLogs();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Client Actions ────────────────────────

  const createClient = async (e) => {
    e.preventDefault();
    if (!newClient.name || !newClient.email) {
      toast.error("Please fill name and email");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/clients`, newClient, { headers });
      toast.success("Client created! Certificate ready to download.");
      setCreatedClient(res.data);
      setEmailTo(newClient.email);
      setShowEmailInput(true);
      setNewClient({ name: "", email: "", initial_discount: 5 });
      fetchClients();
      fetchStats();
      fetchArtistPerformance();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create client");
    } finally {
      setCreating(false);
    }
  };

  const downloadCertificate = async () => {
    if (!createdClient) return;
    const blob = await generateCertificate(createdClient);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PINK_Certificate_${createdClient.name.replace(/\s+/g, "_")}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Certificate downloaded!");
  };

  const isValidEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const sendCertificateEmail = async () => {
    if (!createdClient) return;
    if (!emailTo.trim()) { toast.error("Please enter an email address"); return; }
    if (!isValidEmail(emailTo.trim())) { toast.error("Invalid email address. Please check the format."); return; }
    setSendingEmail(true);
    try {
      const blob = await generateCertificate(createdClient);
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(blob);
      });
      await axios.post(`${API}/admin/send-certificate-email`, {
        recipient_email: emailTo.trim(),
        client_name: createdClient.name,
        client_id: createdClient.id,
        certificate_base64: base64,
      }, { headers });
      toast.success(`Certificate sent to ${emailTo.trim()}! Email anonymized for GDPR.`);
      setShowEmailInput(false);
      setEmailTo("");
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const claimDiscount = async (clientId, clientName, discount) => {
    if (!window.confirm(`Claim ${discount}% discount for ${clientName}? This will reset their discount to 5%.`)) return;
    try {
      const res = await axios.post(`${API}/admin/clients/${clientId}/claim-discount`, { notes: "" }, { headers });
      toast.success(res.data.message);
      fetchClients();
      fetchStats();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim discount");
    }
  };

  // Re-download a client's certificate.
  // Because the original password is bcrypt-hashed (not recoverable), this
  // resets the password to a fresh one and returns the new credentials.
  const reissueCertificate = async (client) => {
    const confirm = window.confirm(
      `Re-issue certificate for "${client.name}"?\n\n` +
      `This will generate a NEW PASSWORD (the old one cannot be recovered).\n` +
      `The client will need to use the new password going forward.`
    );
    if (!confirm) return;
    try {
      const res = await axios.post(
        `${API}/admin/clients/${client.id}/reissue-certificate`,
        {},
        { headers }
      );
      const blob = await generateCertificate(res.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PINK_Certificate_${res.data.name.replace(/\s+/g, "_")}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Certificate re-issued for ${res.data.name}. New password downloaded.`);
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to re-issue certificate");
    }
  };

  const manualAnonymizeEmail = async (client) => {
    const confirm = window.confirm(
      `Anonymize the email for "${client.name}"?\n\n` +
      `Current email: ${client.email}\n\n` +
      `This will mask the email in the database (e.g. joh***@example.com).\n` +
      `Use this if the email could not be sent automatically and you want to ` +
      `apply GDPR data minimisation manually.`
    );
    if (!confirm) return;
    try {
      const res = await axios.post(
        `${API}/admin/clients/${client.id}/anonymize-email`,
        {},
        { headers }
      );
      toast.success(res.data.message);
      fetchClients();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to anonymize email");
    }
  };

  const confirmDeleteClient = (client) => {
    setDeleteTarget(client);
    setDeleteStep(1);
  };

  const proceedDelete = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    try {
      await axios.delete(`${API}/admin/clients/${deleteTarget.id}`, { headers });
      toast.success(`Client '${deleteTarget.name}' deleted`);
      setDeleteTarget(null);
      setDeleteStep(0);
      fetchClients();
      fetchStats();
      fetchArtistPerformance();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete client");
    }
  };

  const cancelDelete = () => { setDeleteTarget(null); setDeleteStep(0); };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/export-data`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PurpleINK_Backup_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Data exported!");
      fetchAuditLogs();
    } catch (err) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const viewHistory = async (clientId) => {
    setHistoryLoading(true);
    setHistoryOpen(true);
    try {
      const res = await axios.get(`${API}/admin/clients/${clientId}/history`, { headers });
      setHistoryData(res.data);
    } catch (err) {
      toast.error("Failed to load history");
      setHistoryOpen(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Referral ──────────────────────────────

  const addReferral = async (e) => {
    e.preventDefault();
    if (!refCode.trim()) { toast.error("Enter a referral code"); return; }
    setAddingRef(true);
    try {
      const res = await axios.post(`${API}/admin/referrals`, { referral_code: refCode, new_client_name: refName || undefined }, { headers });
      toast.success(res.data.message);
      setRefCode(""); setRefName("");
      fetchClients();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add referral");
    } finally { setAddingRef(false); }
  };

  // ── Artist Management ─────────────────────

  const createArtist = async (e) => {
    e.preventDefault();
    if (!newArtist.name || !newArtist.password) {
      toast.error("Please fill all fields"); return;
    }
    setCreatingArtist(true);
    try {
      const res = await axios.post(`${API}/admin/artists`, { name: newArtist.name, password: newArtist.password }, { headers });
      toast.success(`Artist '${newArtist.name}' created! Login: ${res.data.email}`);
      setNewArtist({ name: "", password: "" });
      fetchArtists();
      fetchArtistPerformance();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create artist");
    } finally { setCreatingArtist(false); }
  };

  const deleteArtist = async (artistId, artistName) => {
    if (!window.confirm(`Delete artist "${artistName}"? Their clients will be reassigned to you.`)) return;
    try {
      const res = await axios.delete(`${API}/admin/artists/${artistId}`, { headers });
      toast.success(res.data.message);
      fetchArtists();
      fetchClients();
      fetchArtistPerformance();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete artist");
    }
  };

  const resetArtistPassword = async () => {
    if (!resetPwTarget || !resetPwValue) return;
    if (resetPwValue.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await axios.put(`${API}/admin/artists/${resetPwTarget.id}/reset-password`, { new_password: resetPwValue }, { headers });
      toast.success(`Password reset for '${resetPwTarget.name}'`);
      setResetPwTarget(null);
      setResetPwValue("");
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
  };

  const reassignClient = async () => {
    if (!reassignTarget || !reassignArtistId) return;
    try {
      const res = await axios.put(`${API}/admin/clients/${reassignTarget.id}/reassign`, { artist_id: reassignArtistId }, { headers });
      toast.success(res.data.message);
      setReassignTarget(null);
      setReassignArtistId("");
      fetchClients();
      fetchStats();
      fetchArtistPerformance();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reassign client");
    }
  };

  // ── QR Scanner ─────────────────────────────

  const startScanner = async () => {
    setScanError("");
    setScannerActive(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      // Wait for DOM render
      await new Promise((r) => setTimeout(r, 400));
      const scanner = new Html5Qrcode("qr-reader");
      html5QrRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          try {
            const data = JSON.parse(decodedText);
            if (data.client_id && data.qr_token) {
              await scanner.stop();
              html5QrRef.current = null;
              setScannerActive(false);
              verifyQrCode(data.client_id, data.qr_token);
            }
          } catch (err) {
            console.debug("Non-JSON QR scan ignored:", err?.message);
          }
        },
        () => {} // ignore scan errors
      );
      // Scroll to center after camera is live
      setTimeout(() => {
        const el = document.getElementById("qr-reader");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    } catch (err) {
      setScannerActive(false);
      setScanError(err?.message || "Could not start camera. Please allow camera access.");
    }
  };

  const stopScanner = async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); } catch (err) { console.debug("QR scanner stop:", err?.message); }
      html5QrRef.current = null;
    }
    setScannerActive(false);
  };

  const verifyQrCode = async (clientId, qrToken) => {
    try {
      const res = await axios.post(`${API}/admin/qr-verify`, { client_id: clientId, qr_token: qrToken }, { headers });
      setScannedClient(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid QR code");
      setScannedClient(null);
    }
  };

  const claimFromQr = async () => {
    if (!scannedClient) return;
    setClaimingQr(true);
    try {
      const res = await axios.post(
        `${API}/admin/clients/${scannedClient.client_id}/claim-discount`,
        { notes: "Claimed via QR scan" },
        { headers }
      );
      toast.success(res.data.message);
      setScannedClient(null);
      fetchClients();
      fetchStats();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim discount");
    } finally { setClaimingQr(false); }
  };

  // ── Reviews ───────────────────────────────

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { headers });
      toast.success("Review deleted"); fetchReviews();
    } catch (err) { toast.error("Failed to delete review"); }
  };

  // ── Gallery ───────────────────────────────

  const uploadGalleryImage = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadTitle.trim()) { toast.error("Please add a title and select an image"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle);
      formData.append("category", uploadCategory);
      await axios.post(`${API}/admin/gallery/upload`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded!");
      setUploadTitle(""); setUploadCategory("general"); setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchGallery();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to upload"); }
    finally { setUploading(false); }
  };

  const deleteGalleryItem = async (itemId) => {
    try {
      await axios.delete(`${API}/admin/gallery/${itemId}`, { headers });
      toast.success("Image deleted"); fetchGallery();
    } catch (err) { toast.error("Failed to delete image"); }
  };

  // ── Settings ──────────────────────────────

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {};
      const fields = ["reviews_enabled", "instagram_url", "instagram_handle", "location", "open_hours", "phone", "hero_text", "about_text", "compliance_enabled", "compliance_visibility", "compliance_source_url"];
      for (const f of fields) {
        if (settingsForm[f] !== undefined && settingsForm[f] !== settings[f]) {
          payload[f] = settingsForm[f];
        }
      }
      if (Object.keys(payload).length === 0) { toast("No changes to save"); setSavingSettings(false); return; }
      const res = await axios.put(`${API}/admin/settings`, payload, { headers });
      setSettings(res.data);
      setSettingsForm(res.data);
      toast.success("Settings saved!");
    } catch (err) { toast.error("Failed to save settings"); }
    finally { setSavingSettings(false); }
  };

  const toggleReviews = async (enabled) => {
    try {
      const res = await axios.put(`${API}/admin/settings`, { reviews_enabled: enabled }, { headers });
      setSettings(res.data); setSettingsForm(res.data);
      toast.success(enabled ? "Reviews enabled" : "Reviews disabled");
    } catch (err) { toast.error("Failed to update"); }
  };

  const inputClass = "bg-black/50 border-white/10 focus:border-purple-500/50 text-white placeholder:text-neutral-600 h-12 rounded-lg";

  const changePassword = async () => {
    if (!pwForm.current || !pwForm.newPw) { toast.error("Fill all password fields"); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    if (pwForm.newPw.length < 6) { toast.error("Min 6 characters"); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.newPw }, { headers });
      toast.success("Password changed!");
      setPwForm({ current: "", newPw: "", confirm: "" });
      setShowPasswordChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to change password"); }
    finally { setChangingPw(false); }
  };

  const filteredClients = clients
    .filter((c) => {
      if (artistFilter && c.assigned_artist_id !== artistFilter) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.username?.toLowerCase().includes(q) || c.referral_code?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (!sortKey) return 0;
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortKey === "name") return (a.name || "").localeCompare(b.name || "") * dir;
      if (sortKey === "username") return (a.username || "").localeCompare(b.username || "") * dir;
      if (sortKey === "email") return (a.email || "").localeCompare(b.email || "") * dir;
      if (sortKey === "discount") return ((a.discount || 0) - (b.discount || 0)) * dir;
      if (sortKey === "referrals") return ((a.referral_count || 0) - (b.referral_count || 0)) * dir;
      if (sortKey === "date") return (a.created_date || "").localeCompare(b.created_date || "") * dir;
      return 0;
    });

  // Paginated clients
  const totalClientPages = Math.max(1, Math.ceil(filteredClients.length / ITEMS_PER_PAGE));
  const paginatedClients = filteredClients.slice((clientsPage - 1) * ITEMS_PER_PAGE, clientsPage * ITEMS_PER_PAGE);

  // Filtered + paginated audit logs
  const filteredAudit = auditLogs.filter((l) => {
    if (auditActionFilter && l.action !== auditActionFilter) return false;
    if (auditActorFilter && !l.actor_name?.toLowerCase().includes(auditActorFilter.toLowerCase())) return false;
    return true;
  });
  const totalAuditPages = Math.max(1, Math.ceil(filteredAudit.length / ITEMS_PER_PAGE));
  const paginatedAudit = filteredAudit.slice((auditPage - 1) * ITEMS_PER_PAGE, auditPage * ITEMS_PER_PAGE);

  // Unique audit actions for filter
  const auditActions = [...new Set(auditLogs.map((l) => l.action))].sort();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-[calc(6rem+env(safe-area-inset-top))] pb-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-purple-400 mb-3" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
          {isMaster ? t("admin_master_label") : t("admin_artist_label")}
        </p>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-12" data-testid="admin-title">
          {isMaster ? t("admin_title") : t("admin_welcome", { name: authUser?.name || "Artist" })}
        </h1>

        <Tabs defaultValue="dashboard" className="space-y-8">
          <TabsList className="bg-neutral-900/50 border border-white/5 p-1.5 sm:p-1 h-auto flex-wrap gap-1" data-testid="admin-tabs">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-dashboard"><LayoutDashboard className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_dashboard")}</TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-clients"><Users className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_clients")}</TabsTrigger>
            <TabsTrigger value="create" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-create"><UserPlus className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_new_client")}</TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-referrals"><Link2 className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_referrals")}</TabsTrigger>
            <TabsTrigger value="scanqr" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-scanqr"><QrCode className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_scan_qr")}</TabsTrigger>
            <TabsTrigger value="appointments" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-appointments"><Calendar className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_appointments")}</TabsTrigger>
            {isMaster && <TabsTrigger value="artists" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-artists"><Palette className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_artists")}</TabsTrigger>}
            {isMaster && <TabsTrigger value="gallery" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-gallery"><Image className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_gallery")}</TabsTrigger>}
            {isMaster && <TabsTrigger value="reviews" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-reviews"><Star className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_reviews")}</TabsTrigger>}
            {isMaster && <TabsTrigger value="messages" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-messages"><MessageSquare className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_messages")}</TabsTrigger>}
            {isMaster && <TabsTrigger value="settings" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-settings"><Settings className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_settings")}</TabsTrigger>}
            {isMaster && <TabsTrigger value="audit" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-300 data-[state=active]:border-purple-500/30 text-neutral-400 text-sm sm:text-xs tracking-wider uppercase gap-2 py-3 px-4 sm:py-2 sm:px-3 border border-transparent rounded-lg" data-testid="tab-audit"><Shield className="w-5 h-5 sm:w-4 sm:h-4" /> {t("tab_audit")}</TabsTrigger>}
          </TabsList>

          {/* ── Dashboard Tab ─────────────────────────── */}
          <TabsContent value="dashboard" data-testid="dashboard-tab-content">
            {/* Dashboard Artist Filter (Master Admin) */}
            {isMaster && (
              <div className="mb-6 flex items-center gap-3">
                <Filter className="w-4 h-4 text-neutral-500" />
                <select
                  value={dashboardArtistFilter}
                  onChange={(e) => { setDashboardArtistFilter(e.target.value); fetchStats(e.target.value || undefined); }}
                  className="bg-black/50 border border-white/10 text-neutral-300 text-sm rounded-lg h-10 px-3 focus:border-purple-500/50 outline-none min-w-[200px]"
                  data-testid="dashboard-artist-filter"
                >
                  <option value="">{t("filter_all_artists")}</option>
                  {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  <option value={authUser?.id}>{t("stanelo_master")}</option>
                </select>
              </div>
            )}
            {stats ? (
              <div className="space-y-8">
                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: t("stat_total_clients"), value: stats.total_clients, color: "purple", icon: <Users className="w-5 h-5" /> },
                    { label: t("stat_active_discounts"), value: stats.active_discounts, sub: "> 5%", color: "pink", icon: <TrendingUp className="w-5 h-5" /> },
                    { label: t("stat_total_referrals"), value: stats.total_referrals, color: "cyan", icon: <Link2 className="w-5 h-5" /> },
                    { label: t("stat_referrals_month"), value: stats.referrals_this_month, color: "emerald", icon: <Star className="w-5 h-5" /> },
                  ].map((s) => (
                    <div key={s.label} className={`glass-card rounded-xl p-5 border border-${s.color}-500/20`} data-testid={`stat-card-${s.label}`}>
                      <div className={`flex items-center gap-2 text-${s.color}-400 mb-3`}>
                        {s.icon}
                        <span className="text-xs tracking-wider uppercase font-bold">{s.label}</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{s.value}</p>
                      {s.sub && <p className="text-xs text-neutral-500 mt-1">{s.sub}</p>}
                    </div>
                  ))}
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* New Clients Chart (last 30 days) */}
                  <div className="glass-card rounded-xl p-6 border border-white/5">
                    <h3 className="text-sm font-bold text-neutral-300 uppercase tracking-wider mb-4">{t("stat_new_clients_30")}</h3>
                    {Object.keys(stats.clients_by_day).length === 0 ? (
                      <p className="text-neutral-600 text-sm">{t("chart_no_new")}</p>
                    ) : (
                      <div className="flex items-end gap-1 h-32">
                        {(() => {
                          const days = [];
                          const now = new Date();
                          for (let i = 29; i >= 0; i--) {
                            const d = new Date(now);
                            d.setDate(d.getDate() - i);
                            const key = d.toISOString().slice(0, 10);
                            days.push({ key, count: stats.clients_by_day[key] || 0 });
                          }
                          const max = Math.max(1, ...days.map((d) => d.count));
                          return days.map((d) => (
                            <div key={d.key} className="flex-1 flex flex-col items-center justify-end h-full group" title={`${d.key}: ${d.count}`}>
                              <div
                                className="w-full bg-purple-500/60 rounded-t group-hover:bg-purple-400 transition-colors min-h-[2px]"
                                style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
                              />
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                    <div className="flex justify-between mt-2">
                      <span className="text-[10px] text-neutral-600">{t("chart_30d")}</span>
                      <span className="text-[10px] text-neutral-600">{t("chart_today")}</span>
                    </div>
                  </div>

                  {/* Discount Distribution */}
                  <div className="glass-card rounded-xl p-6 border border-white/5">
                    <h3 className="text-sm font-bold text-neutral-300 uppercase tracking-wider mb-4">{t("stat_discount_dist")}</h3>
                    {Object.keys(stats.discount_distribution).length === 0 ? (
                      <p className="text-neutral-600 text-sm">{t("chart_no_clients")}</p>
                    ) : (
                      <div className="space-y-3">
                        {discountDistRows.map(({ disc, count, pct }) => (
                          <div key={disc} className="flex items-center gap-3">
                            <span className="text-xs text-purple-300 w-10 text-right font-mono">{disc}%</span>
                            <div className="flex-1 h-5 bg-black/50 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-purple-600 to-pink-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-neutral-400 w-8">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Activity Chart + Recent Activity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Activity last 7 days */}
                  <div className="glass-card rounded-xl p-6 border border-white/5">
                    <h3 className="text-sm font-bold text-neutral-300 uppercase tracking-wider mb-4">{t("stat_activity_7")}</h3>
                    <div className="flex items-end gap-2 h-28">
                      {(() => {
                        const days = [];
                        const now = new Date();
                        for (let i = 6; i >= 0; i--) {
                          const d = new Date(now);
                          d.setDate(d.getDate() - i);
                          const key = d.toISOString().slice(0, 10);
                          const dayLabel = d.toLocaleDateString("en", { weekday: "short" });
                          days.push({ key, label: dayLabel, count: stats.audit_by_day[key] || 0 });
                        }
                        const max = Math.max(1, ...days.map((d) => d.count));
                        return days.map((d, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-[10px] text-neutral-500">{d.count || ""}</span>
                            <div className="w-full flex items-end h-20">
                              <div
                                className="w-full bg-emerald-500/50 rounded-t hover:bg-emerald-400/70 transition-colors min-h-[2px]"
                                style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-neutral-600">{d.label}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Recent Activity Feed */}
                  <div className="glass-card rounded-xl p-6 border border-white/5">
                    <h3 className="text-sm font-bold text-neutral-300 uppercase tracking-wider mb-4">{t("stat_recent_activity")}</h3>
                    <div className="space-y-3">
                      {stats.recent_activity.map((a, i) => (
                        <div key={i} className="flex items-start gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 shrink-0" />
                          <div>
                            <span className="text-neutral-300">{a.details}</span>
                            <p className="text-xs text-neutral-600">{new Date(a.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </TabsContent>

          {/* ── Clients Tab ──────────────────────────── */}
          <TabsContent value="clients" data-testid="clients-tab-content">
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setClientsPage(1); }}
                  placeholder={t("clients_search")}
                  className="bg-black/50 border-white/10 focus:border-purple-500/50 text-white placeholder:text-neutral-600 h-10 rounded-lg pl-10 text-sm"
                  data-testid="client-search-input"
                />
              </div>
              {isMaster && (
                <select
                  value={artistFilter}
                  onChange={(e) => { setArtistFilter(e.target.value); setClientsPage(1); }}
                  className="bg-black/50 border border-white/10 text-neutral-300 text-xs rounded-lg h-10 px-3 focus:border-purple-500/50 outline-none"
                  data-testid="artist-filter-select"
                >
                  <option value="">{t("clients_all_artists")}</option>
                  {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  <option value={authUser?.id}>{t("clients_master")}</option>
                </select>
              )}
              {isMaster && (
                <Button
                  onClick={exportData}
                  disabled={exporting}
                  className="bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-500/30 text-xs tracking-wider uppercase gap-2 h-10"
                  data-testid="export-data-btn"
                >
                  <FileDown className="w-4 h-4" />{exporting ? t("clients_exporting") : t("clients_export")}
                </Button>
              )}
            </div>
            <div className="glass-card rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-neutral-800 hover:bg-transparent">
                    {[
                      ["name", t("col_name")],
                      ["username", t("col_username")],
                      ["email", t("col_email")],
                      ["discount", t("col_discount")],
                      ["referrals", t("col_referrals")],
                    ].map(([key, label]) => (
                      <TableHead key={key} className="text-xs uppercase tracking-wider text-neutral-500 cursor-pointer hover:text-purple-400 select-none" onClick={() => toggleSort(key)} data-testid={`sort-${key}`}>
                        <span className="inline-flex items-center gap-1">
                          {label}
                          {renderSortIcon(key)}
                        </span>
                      </TableHead>
                    ))}
                    <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_code")}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_artist")}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-neutral-500 cursor-pointer hover:text-purple-400 select-none" onClick={() => toggleSort("date")} data-testid="sort-date">
                      <span className="inline-flex items-center gap-1">
                        {t("col_date")}
                        {renderSortIcon("date")}
                      </span>
                    </TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_consent")}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-neutral-500 text-right">{t("col_actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedClients.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center text-neutral-500 py-8">{searchQuery ? t("no_clients_search") : t("no_clients")}</TableCell></TableRow>
                  ) : (
                    paginatedClients.map((client, idx) => (
                      <TableRow key={client.id || idx} className="border-b border-neutral-900 hover:bg-white/5" data-testid={`client-row-${idx}`}>
                        <TableCell className="text-neutral-300">{client.name}</TableCell>
                        <TableCell className="text-purple-300 text-sm font-mono">{client.username || "—"}</TableCell>
                        <TableCell className="text-neutral-400 text-sm">{client.email}</TableCell>
                        <TableCell><span className="text-purple-400 font-bold">{client.discount}%</span></TableCell>
                        <TableCell className="text-neutral-300">{client.referral_count}</TableCell>
                        <TableCell><code className="text-xs text-purple-300 bg-purple-500/10 px-2 py-1 rounded">{client.referral_code}</code></TableCell>
                        <TableCell>
                          <span className="text-xs text-pink-300 bg-pink-500/10 px-2 py-1 rounded" data-testid={`client-artist-${idx}`}>
                            {client.assigned_artist_name || t("unassigned")}
                          </span>
                        </TableCell>
                        <TableCell className="text-neutral-500 text-xs">{client.created_date || "—"}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-1 rounded ${client.gdpr_consent ? "text-emerald-300 bg-emerald-500/10" : "text-amber-300 bg-amber-500/10"}`} data-testid={`client-consent-${idx}`}>
                            {client.gdpr_consent ? t("consent_yes") : t("consent_no")}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => claimDiscount(client.id, client.name, client.discount)}
                              disabled={client.discount <= 0}
                              className="inline-flex items-center gap-1 bg-pink-600/20 hover:bg-pink-600/40 text-pink-300 text-xs px-3 py-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Mark discount as used and reset"
                              data-testid={`claim-discount-${idx}`}
                            >
                              <TicketCheck className="w-3 h-3" /> {t("btn_claim")}
                            </button>
                            <button
                              onClick={() => viewHistory(client.id)}
                              className="inline-flex items-center gap-1 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 text-xs px-3 py-1.5 rounded transition-colors"
                              data-testid={`view-history-${idx}`}
                            >
                              <History className="w-3 h-3" /> {t("btn_history")}
                            </button>
                            <button
                              onClick={() => reissueCertificate(client)}
                              className="inline-flex items-center gap-1 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 text-xs px-3 py-1.5 rounded transition-colors"
                              title="Re-issue certificate (generates a new password)"
                              data-testid={`reissue-certificate-${idx}`}
                            >
                              <Download className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => manualAnonymizeEmail(client)}
                              disabled={!client.email || client.email.includes("*")}
                              className="inline-flex items-center gap-1 bg-amber-600/20 hover:bg-amber-600/40 text-amber-300 text-xs px-3 py-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Anonymize this client's email (GDPR — manual)"
                              data-testid={`anonymize-email-${idx}`}
                            >
                              <EyeOff className="w-3 h-3" />
                            </button>
                            {isMaster && (
                              <button
                                onClick={() => { setReassignTarget(client); setReassignArtistId(""); }}
                                className="inline-flex items-center gap-1 bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-300 text-xs px-3 py-1.5 rounded transition-colors"
                                title="Reassign to different artist"
                                data-testid={`reassign-client-${idx}`}
                              >
                                <ArrowRightLeft className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={() => confirmDeleteClient(client)}
                              className="inline-flex items-center gap-1 bg-red-600/20 hover:bg-red-600/40 text-red-300 text-xs px-3 py-1.5 rounded transition-colors"
                              title="Delete this client"
                              data-testid={`delete-client-${idx}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            {totalClientPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-2" data-testid="clients-pagination">
                <span className="text-xs text-neutral-500">{filteredClients.length} clients · Page {clientsPage} of {totalClientPages}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setClientsPage((p) => Math.max(1, p - 1))} disabled={clientsPage === 1} className="p-2 rounded bg-white/5 hover:bg-white/10 text-neutral-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="clients-prev"><ChevronLeft className="w-4 h-4" /></button>
                  {Array.from({ length: Math.min(5, totalClientPages) }, (_, i) => {
                    let page;
                    if (totalClientPages <= 5) page = i + 1;
                    else if (clientsPage <= 3) page = i + 1;
                    else if (clientsPage >= totalClientPages - 2) page = totalClientPages - 4 + i;
                    else page = clientsPage - 2 + i;
                    return (
                      <button key={page} onClick={() => setClientsPage(page)} className={`w-8 h-8 rounded text-xs font-bold transition-colors ${clientsPage === page ? "bg-purple-600 text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"}`} data-testid={`clients-page-${page}`}>{page}</button>
                    );
                  })}
                  <button onClick={() => setClientsPage((p) => Math.min(totalClientPages, p + 1))} disabled={clientsPage === totalClientPages} className="p-2 rounded bg-white/5 hover:bg-white/10 text-neutral-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="clients-next"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="create" data-testid="create-tab-content">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass-card p-8 rounded-xl">
                <h3 className="text-lg font-semibold mb-2">{t("new_client_title")}</h3>
                <p className="text-xs text-neutral-500 mb-6">{t("new_client_desc")}</p>
                <form onSubmit={createClient} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("new_client_name")}</Label>
                    <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="Client name" className={inputClass} data-testid="create-client-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("new_client_email")}</Label>
                    <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="client@email.com" className={inputClass} data-testid="create-client-email" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("new_client_discount")}</Label>
                    <Input type="number" min="0" max="65" value={newClient.initial_discount} onChange={(e) => setNewClient({ ...newClient, initial_discount: parseInt(e.target.value) || 0 })} className={inputClass} data-testid="create-client-discount" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("col_date")}</Label>
                    <Input type="text" value={new Date().toLocaleDateString("en-CA")} readOnly className="bg-black/50 border-white/10 text-neutral-400 h-12 rounded-lg cursor-not-allowed" data-testid="create-client-date" />
                  </div>
                  <Button type="submit" disabled={creating} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12" data-testid="create-client-submit">
                    <UserPlus className="w-4 h-4 mr-2" />{creating ? t("new_client_creating") : t("new_client_submit")}
                  </Button>
                </form>
              </div>

              {createdClient && (
                <div className="glass-card p-8 rounded-xl animate-fade-in-up" data-testid="certificate-panel">
                  <h3 className="text-lg font-semibold mb-2">{t("new_client_creds")}</h3>
                  <p className="text-xs text-neutral-500 mb-6">{t("new_client_creds_desc")}</p>
                  <div className="bg-neutral-950 border border-purple-500/20 rounded-lg p-6 mb-6 space-y-4">
                    {[
                      [t("scan_client"), createdClient.name, "cert-client-name"],
                      [t("new_client_username"), createdClient.username, "cert-client-username"],
                    ].map(([label, val, tid]) => (
                      <div key={tid} className="flex justify-between items-center">
                        <span className="text-xs text-neutral-500 uppercase tracking-wider">{label}</span>
                        <span className="text-sm text-neutral-200 font-mono" data-testid={tid}>{val}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("new_client_password")}</span>
                      <span className="text-sm text-purple-400 font-mono font-bold" data-testid="cert-client-password">{createdClient.plain_password}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("col_discount")}</span>
                      <span className="text-sm text-purple-400 font-bold">{createdClient.discount}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("col_code")}</span>
                      <code className="text-xs text-purple-300 bg-purple-500/10 px-2 py-1 rounded">{createdClient.referral_code}</code>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("col_date")}</span>
                      <span className="text-sm text-neutral-400">{createdClient.created_date}</span>
                    </div>
                  </div>
                  <Button onClick={downloadCertificate} className="w-full bg-pink-600 hover:bg-pink-500 text-white font-bold py-3 border border-pink-400 shadow-[0_0_20px_rgba(219,39,119,0.3)] hover:shadow-[0_0_30px_rgba(219,39,119,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12" data-testid="download-certificate-btn">
                    <Download className="w-4 h-4 mr-2" /> {t("new_client_download")}
                  </Button>

                  {!showEmailInput ? (
                    <Button
                      onClick={() => { setShowEmailInput(true); setEmailTo(createdClient.email || ""); }}
                      className="w-full mt-3 bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12"
                      data-testid="show-email-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" /> {t("new_client_send_email")}
                    </Button>
                  ) : (
                    <div className="mt-3 space-y-3 animate-fade-in-up" data-testid="email-certificate-form">
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={emailTo}
                          onChange={(e) => setEmailTo(e.target.value)}
                          placeholder="client@email.com"
                          className={`${inputClass} flex-1`}
                          data-testid="email-certificate-input"
                        />
                        <Button
                          onClick={() => { setShowEmailInput(false); setEmailTo(""); }}
                          variant="ghost"
                          className="text-neutral-500 hover:text-white px-3"
                          data-testid="cancel-email-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {emailTo && !isValidEmail(emailTo) && (
                        <p className="text-xs text-red-400" data-testid="email-validation-error">
                          {t("new_client_email_invalid")}
                        </p>
                      )}
                      <Button
                        onClick={sendCertificateEmail}
                        disabled={sendingEmail || !isValidEmail(emailTo)}
                        className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="send-email-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />{sendingEmail ? t("new_client_sending") : t("new_client_send_cert")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Referrals Tab ────────────────────────── */}
          <TabsContent value="referrals" data-testid="referrals-tab-content">
            <div className="glass-card p-8 rounded-xl max-w-lg">
              <h3 className="text-lg font-semibold mb-6">{t("referrals_title")}</h3>
              <p className="text-sm text-neutral-400 mb-6">{t("referrals_desc")}</p>
              <form onSubmit={addReferral} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("referrals_code")}</Label>
                  <Input value={refCode} onChange={(e) => setRefCode(e.target.value)} placeholder="PINK-XXXXXXXX" className={`${inputClass} font-mono`} data-testid="referral-code-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("referrals_name")}</Label>
                  <Input value={refName} onChange={(e) => setRefName(e.target.value)} placeholder="Name of referred person" className={inputClass} data-testid="referral-name-input" />
                </div>
                <Button type="submit" disabled={addingRef} className="w-full bg-pink-600 hover:bg-pink-500 text-white font-bold py-3 border border-pink-400 shadow-[0_0_20px_rgba(219,39,119,0.3)] hover:shadow-[0_0_30px_rgba(219,39,119,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12" data-testid="add-referral-submit">
                  <Link2 className="w-4 h-4 mr-2" />{addingRef ? t("referrals_adding") : t("referrals_submit")}
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* ── Scan QR Tab ──────────────────────────── */}
          <TabsContent value="scanqr" data-testid="scanqr-tab-content">
            <div className="max-w-lg mx-auto">
              <div className="glass-card p-6 sm:p-8 rounded-xl text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Camera className="w-6 h-6 text-purple-400" />
                  <h3 className="text-lg font-semibold">{t("scan_title")}</h3>
                </div>
                <p className="text-xs text-neutral-500 mb-6">
                  {t("scan_desc")}
                </p>

                {!scannerActive && !scannedClient && (
                  <div>
                    <button
                      onClick={startScanner}
                      className="w-full flex items-center justify-center gap-3 px-6 py-5 sm:py-4 bg-purple-600 hover:bg-purple-500 text-white text-base sm:text-sm font-bold rounded-xl transition-all duration-300 shadow-[0_0_25px_rgba(192,38,211,0.3)] hover:shadow-[0_0_40px_rgba(192,38,211,0.6)] uppercase tracking-widest"
                      data-testid="start-scanner-btn"
                    >
                      <Camera className="w-6 h-6 sm:w-5 sm:h-5" /> {t("scan_start")}
                    </button>
                    {scanError && (
                      <p className="text-xs text-red-400 mt-3" data-testid="scan-error">{scanError}</p>
                    )}
                  </div>
                )}

                {scannerActive && (
                  <div className="flex flex-col items-center">
                    <div
                      id="qr-reader"
                      ref={scannerRef}
                      className="rounded-xl overflow-hidden mb-4 w-full"
                      style={{ maxWidth: 350 }}
                      data-testid="qr-reader"
                    />
                    <button
                      onClick={stopScanner}
                      className="inline-flex items-center gap-2 px-6 py-3 sm:py-2.5 bg-red-600/20 hover:bg-red-600/40 text-red-300 text-sm sm:text-xs rounded-lg transition-colors border border-red-500/30"
                      data-testid="stop-scanner-btn"
                    >
                      <X className="w-4 h-4" /> {t("scan_stop")}
                    </button>
                  </div>
                )}

                {/* Scanned Client Result */}
                {scannedClient && (
                  <div className="mt-6 p-6 bg-neutral-900/80 border border-purple-500/30 rounded-xl text-left" data-testid="scanned-client-result">
                    <div className="flex items-center gap-2 mb-4">
                      <Check className="w-5 h-5 text-emerald-400" />
                      <span className="text-sm font-semibold text-emerald-400">{t("scan_verified")}</span>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between">
                        <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("scan_client")}</span>
                        <span className="text-sm font-semibold text-neutral-200">{scannedClient.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("scan_discount")}</span>
                        <span className="text-sm font-bold text-purple-400">{scannedClient.discount}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("scan_referrals")}</span>
                        <span className="text-sm text-neutral-300">{scannedClient.referral_count}</span>
                      </div>
                      {scannedClient.assigned_artist_name && (
                        <div className="flex justify-between">
                          <span className="text-xs text-neutral-500 uppercase tracking-wider">{t("scan_artist")}</span>
                          <span className="text-sm text-pink-300">{scannedClient.assigned_artist_name}</span>
                        </div>
                      )}
                    </div>

                    {scannedClient.discount > 0 ? (
                      <div className="space-y-3">
                        <p className="text-xs text-neutral-400 text-center">
                          {t("scan_claim_q", { pct: scannedClient.discount })}
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setScannedClient(null)}
                            className="flex-1 px-4 py-4 sm:py-3 bg-transparent border border-white/10 hover:bg-white/5 text-neutral-300 text-sm sm:text-xs font-semibold rounded-lg transition-colors"
                            data-testid="qr-cancel-btn"
                          >
                            {t("scan_cancel")}
                          </button>
                          <button
                            onClick={claimFromQr}
                            disabled={claimingQr}
                            className="flex-1 px-4 py-4 sm:py-3 bg-pink-600 hover:bg-pink-500 text-white text-sm sm:text-xs font-bold rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(219,39,119,0.3)] hover:shadow-[0_0_30px_rgba(219,39,119,0.5)] uppercase tracking-wider disabled:opacity-50"
                            data-testid="qr-claim-btn"
                          >
                            <TicketCheck className="w-4 h-4 inline mr-1" />{claimingQr ? t("scan_claiming") : t("scan_claim_btn", { pct: scannedClient.discount })}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="text-xs text-neutral-500 mb-3">{t("scan_no_discount")}</p>
                        <button
                          onClick={() => setScannedClient(null)}
                          className="px-6 py-3 sm:py-2.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-sm sm:text-xs rounded-lg transition-colors"
                          data-testid="qr-close-btn"
                        >
                          {t("scan_another")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── Appointments Tab ────────────────────────── */}
          <TabsContent value="appointments" data-testid="appointments-tab-content">
            <AppointmentsTab artists={artists} isMaster={isMaster} authUser={authUser} />
          </TabsContent>

          {/* ── Artists Tab (Master Admin Only) ──────── */}
          {isMaster && (
            <TabsContent value="artists" data-testid="artists-tab-content">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="glass-card p-8 rounded-xl">
                  <h3 className="text-lg font-semibold mb-2">{t("artists_add")}</h3>
                  <p className="text-xs text-neutral-500 mb-6">{t("artists_add_desc")} <span className="text-purple-400">name@pink.ro</span></p>
                  <form onSubmit={createArtist} className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("artists_name")}</Label>
                      <Input value={newArtist.name} onChange={(e) => setNewArtist({ ...newArtist, name: e.target.value })} placeholder="Artist name" className={inputClass} data-testid="create-artist-name" />
                      {newArtist.name && (
                        <p className="text-xs text-purple-400 mt-1">
                          {t("artists_login_email")}: <span className="font-mono">{newArtist.name.toLowerCase().replace(/[^a-z0-9]/g, '')}@pink.ro</span>
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("artists_password")}</Label>
                      <Input type="text" value={newArtist.password} onChange={(e) => setNewArtist({ ...newArtist, password: e.target.value })} placeholder="Set password" className={inputClass} data-testid="create-artist-password" />
                    </div>
                    <Button type="submit" disabled={creatingArtist} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12" data-testid="create-artist-submit">
                      <Palette className="w-4 h-4 mr-2" />{creatingArtist ? t("artists_creating") : t("artists_create")}
                    </Button>
                  </form>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">{t("artists_active")} ({artists.length})</h3>
                  {artists.length === 0 ? (
                    <div className="glass-card p-8 rounded-xl text-center text-neutral-500">{t("artists_no")}</div>
                  ) : (
                    <div className="space-y-3">
                      {artists.map((artist) => (
                        <div key={artist.id} className="glass-card p-5 rounded-xl border border-white/5 hover:border-purple-500/20 transition-colors" data-testid={`artist-card-${artist.id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-neutral-200">{artist.name}</p>
                              <p className="text-xs text-neutral-500 mt-0.5">{artist.email}</p>
                              <p className="text-xs text-purple-400 mt-1">
                                {artist.client_count} {artist.client_count !== 1 ? t("artists_clients_pl") : t("artists_clients")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => { setResetPwTarget(artist); setResetPwValue(""); }}
                                className="inline-flex items-center gap-1 bg-amber-600/20 hover:bg-amber-600/40 text-amber-300 text-xs px-3 py-1.5 rounded transition-colors"
                                data-testid={`reset-pw-artist-${artist.id}`}
                              >
                                <Lock className="w-3 h-3" /> {t("artists_password_btn")}
                              </button>
                              <button
                                onClick={() => deleteArtist(artist.id, artist.name)}
                                className="inline-flex items-center gap-1 bg-red-600/20 hover:bg-red-600/40 text-red-300 text-xs px-3 py-1.5 rounded transition-colors"
                                data-testid={`delete-artist-${artist.id}`}
                              >
                                <Trash2 className="w-3 h-3" /> {t("artists_remove")}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Per-Artist Performance Report */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">{t("artists_performance")}</h3>
                <div className="glass-card rounded-xl overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-neutral-800 hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("perf_artist")}</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("perf_role")}</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("perf_clients")}</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("perf_referrals")}</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("perf_discounts")}</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("perf_claims")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {artistPerformance.map((perf) => (
                        <TableRow key={perf.id} className="border-b border-neutral-900 hover:bg-white/5" data-testid={`perf-row-${perf.id}`}>
                          <TableCell>
                            <div>
                              <p className="text-neutral-200 text-sm font-semibold">{perf.name}</p>
                              <p className="text-neutral-500 text-xs">{perf.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded ${perf.role === "master_admin" ? "bg-purple-500/20 text-purple-300" : "bg-pink-500/20 text-pink-300"}`}>
                              {perf.role === "master_admin" ? t("perf_master") : t("perf_artist_role")}
                            </span>
                          </TableCell>
                          <TableCell className="text-neutral-300 font-bold">{perf.client_count}</TableCell>
                          <TableCell className="text-purple-400">{perf.total_referrals}</TableCell>
                          <TableCell className="text-pink-400">{perf.active_discounts}</TableCell>
                          <TableCell className="text-emerald-400">{perf.total_claims}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          )}

          {/* ── Gallery Tab ──────────────────────────── */}
          <TabsContent value="gallery" data-testid="gallery-tab-content">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass-card p-8 rounded-xl">
                <h3 className="text-lg font-semibold mb-6">{t("gallery_upload")}</h3>
                <form onSubmit={uploadGalleryImage} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("gallery_title_label")}</Label>
                    <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder="Image title" className={inputClass} data-testid="gallery-upload-title" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("gallery_category")}</Label>
                    <Input value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} placeholder="e.g. Blackwork, Realism" className={inputClass} data-testid="gallery-upload-category" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("gallery_image_file")}</Label>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="w-full h-12 bg-black/50 border border-white/10 rounded-lg text-neutral-400 text-sm file:mr-4 file:py-3 file:px-4 file:border-0 file:text-sm file:bg-purple-600/20 file:text-purple-300 file:cursor-pointer cursor-pointer" data-testid="gallery-upload-file" />
                    {uploadFile && <p className="text-xs text-neutral-500">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)</p>}
                  </div>
                  <Button type="submit" disabled={uploading} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12" data-testid="gallery-upload-submit">
                    <Upload className="w-4 h-4 mr-2" />{uploading ? t("gallery_uploading") : t("gallery_upload_btn")}
                  </Button>
                </form>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">{t("gallery_current")} ({galleryItems.length})</h3>
                {galleryItems.length === 0 ? (
                  <div className="glass-card p-8 rounded-xl text-center text-neutral-500">{t("gallery_no_images")}</div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 max-h-[600px] overflow-y-auto pr-2">
                    {galleryItems.map((item) => (
                      <div key={item.id} className="relative group rounded-lg overflow-hidden border border-white/5 hover:border-purple-500/30 transition-all duration-300" data-testid={`gallery-admin-item-${item.id}`}>
                        <img src={`${BACKEND_URL}${item.image_url}`} alt={item.title} className="w-full aspect-square object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2">
                          <p className="text-xs text-white font-semibold">{item.title}</p>
                          <p className="text-xs text-purple-300">{item.category}</p>
                          <button onClick={() => deleteGalleryItem(item.id)} className="mt-2 flex items-center gap-1 bg-red-600/80 hover:bg-red-500 text-white text-xs px-3 py-1.5 rounded transition-colors" data-testid={`gallery-delete-${item.id}`}>
                            <Trash2 className="w-3 h-3" /> {t("gallery_remove")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── Reviews Tab ──────────────────────────── */}
          <TabsContent value="reviews" data-testid="reviews-tab-content">
            <div className="glass-card rounded-xl overflow-x-auto">
              {reviews.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">{t("admin_reviews_no")}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-neutral-800 hover:bg-transparent">
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("scan_client")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_discount")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("tab_reviews")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_date")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500 w-16">{t("col_actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.map((review, idx) => (
                      <TableRow key={review.id || idx} className="border-b border-neutral-900 hover:bg-white/5" data-testid={`review-row-${idx}`}>
                        <TableCell className="text-neutral-300">{review.user_name}</TableCell>
                        <TableCell>
                          <div className="flex gap-0.5">{[...Array(5)].map((_, i) => (<Star key={`star-${i}`} className={`w-3 h-3 ${i < review.rating ? "text-purple-400 fill-purple-400" : "text-neutral-700"}`} />))}</div>
                        </TableCell>
                        <TableCell className="text-neutral-400 text-sm max-w-xs truncate">{review.text}</TableCell>
                        <TableCell className="text-neutral-500 text-xs">{new Date(review.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <button onClick={() => deleteReview(review.id)} className="text-neutral-600 hover:text-red-400 transition-colors" data-testid={`delete-review-${idx}`}><Trash2 className="w-4 h-4" /></button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* ── Messages Tab ─────────────────────────── */}
          <TabsContent value="messages" data-testid="messages-tab-content">
            <div className="glass-card rounded-xl overflow-x-auto">
              {contacts.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">{t("messages_no")}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-neutral-800 hover:bg-transparent">
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_name")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_email")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("tab_messages")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("col_date")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((msg, idx) => (
                      <TableRow key={msg.id || idx} className="border-b border-neutral-900 hover:bg-white/5" data-testid={`message-row-${idx}`}>
                        <TableCell className="text-neutral-300">{msg.name}</TableCell>
                        <TableCell className="text-neutral-400">{msg.email}</TableCell>
                        <TableCell className="text-neutral-400 text-sm max-w-xs truncate">{msg.message}</TableCell>
                        <TableCell className="text-neutral-500 text-xs">{new Date(msg.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* ── Settings Tab ─────────────────────────── */}
          <TabsContent value="settings" data-testid="settings-tab-content">
            <div className="glass-card p-8 rounded-xl max-w-2xl space-y-8">
              <h3 className="text-lg font-semibold">{t("settings_title")}</h3>

              {/* Reviews toggle */}
              <div className="flex items-center justify-between py-4 border-b border-neutral-800">
                <div>
                  <p className="text-sm font-semibold">{t("settings_reviews_section")}</p>
                  <p className="text-xs text-neutral-500 mt-1">{t("settings_reviews_desc")}</p>
                </div>
                <Switch checked={settingsForm.reviews_enabled ?? true} onCheckedChange={toggleReviews} data-testid="reviews-toggle" />
              </div>

              {/* Daily Ink Compliance */}
              {isMaster && (
                <div className="space-y-4 py-4 border-b border-neutral-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">Daily Ink Compliance</p>
                      <p className="text-xs text-neutral-500 mt-1">Show the daily legal & hygiene briefing page. Content is auto-refreshed once per day from the linked ChatGPT share.</p>
                    </div>
                    <Switch
                      checked={settingsForm.compliance_enabled ?? false}
                      onCheckedChange={(v) => setSettingsForm({ ...settingsForm, compliance_enabled: v })}
                      data-testid="compliance-toggle"
                    />
                  </div>
                  {(settingsForm.compliance_enabled ?? false) && (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-neutral-400">Visibility</p>
                          <p className="text-[11px] text-neutral-600 mt-0.5">Public = anyone can see the page. Login-only = signed-in users only.</p>
                        </div>
                        <div className="inline-flex bg-black/50 border border-white/10 rounded-lg p-1 text-xs" data-testid="compliance-visibility">
                          {["public", "login"].map((v) => (
                            <button
                              key={v}
                              onClick={() => setSettingsForm({ ...settingsForm, compliance_visibility: v })}
                              className={`px-3 py-1 rounded-md transition-colors capitalize ${
                                (settingsForm.compliance_visibility ?? "public") === v
                                  ? "bg-purple-600 text-white"
                                  : "text-neutral-400 hover:text-white"
                              }`}
                              data-testid={`compliance-visibility-${v}`}
                            >{v}</button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs tracking-wider uppercase text-neutral-400">ChatGPT source URL</Label>
                        <Input
                          value={settingsForm.compliance_source_url || ""}
                          onChange={(e) => setSettingsForm({ ...settingsForm, compliance_source_url: e.target.value })}
                          placeholder="https://chatgpt.com/share/..."
                          className={inputClass}
                          data-testid="compliance-source-url"
                        />
                        <p className="text-[11px] text-neutral-600">Leave empty to use the default configured URL.</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Instagram */}
              <div className="space-y-4">
                <p className="text-sm font-semibold text-purple-400 uppercase tracking-wider">{t("settings_social")}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("settings_instagram_url")}</Label>
                    <Input value={settingsForm.instagram_url || ""} onChange={(e) => setSettingsForm({ ...settingsForm, instagram_url: e.target.value })} placeholder="https://instagram.com/..." className={inputClass} data-testid="settings-instagram-url" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("settings_instagram_handle")}</Label>
                    <Input value={settingsForm.instagram_handle || ""} onChange={(e) => setSettingsForm({ ...settingsForm, instagram_handle: e.target.value })} placeholder="@studio_name" className={inputClass} data-testid="settings-instagram-handle" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("settings_location")}</Label>
                    <Input value={settingsForm.location || ""} onChange={(e) => setSettingsForm({ ...settingsForm, location: e.target.value })} placeholder="City, Area" className={inputClass} data-testid="settings-location" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("settings_open_hours")}</Label>
                    <Input value={settingsForm.open_hours || ""} onChange={(e) => setSettingsForm({ ...settingsForm, open_hours: e.target.value })} placeholder="Mon - Fri, 10:00 - 19:00" className={inputClass} data-testid="settings-hours" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("settings_phone")}</Label>
                  <Input value={settingsForm.phone || ""} onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })} placeholder="+40 7XX XXX XXX" className={inputClass} data-testid="settings-phone" />
                </div>
              </div>

              {/* Page Content */}
              <div className="space-y-4">
                <p className="text-sm font-semibold text-purple-400 uppercase tracking-wider">{t("settings_page_content")}</p>
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("settings_hero_desc")}</Label>
                  <Textarea value={settingsForm.hero_text || ""} onChange={(e) => setSettingsForm({ ...settingsForm, hero_text: e.target.value })} placeholder="Hero section text..." className="bg-black/50 border-white/10 focus:border-purple-500/50 text-white placeholder:text-neutral-600 min-h-[80px] rounded-lg" data-testid="settings-hero-text" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("settings_about_text")}</Label>
                  <Textarea value={settingsForm.about_text || ""} onChange={(e) => setSettingsForm({ ...settingsForm, about_text: e.target.value })} placeholder="About section text..." className="bg-black/50 border-white/10 focus:border-purple-500/50 text-white placeholder:text-neutral-600 min-h-[80px] rounded-lg" data-testid="settings-about-text" />
                </div>
              </div>

              <Button onClick={saveSettings} disabled={savingSettings} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs h-12" data-testid="save-settings-btn">
                <Save className="w-4 h-4 mr-2" />{savingSettings ? t("settings_saving") : t("settings_save")}
              </Button>

              {/* Password Change */}
              <div className="space-y-4 pt-4 border-t border-neutral-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">{t("settings_admin_pw")}</p>
                    <p className="text-xs text-neutral-500 mt-1">{t("settings_admin_pw_desc")}</p>
                  </div>
                  <Button variant="outline" onClick={() => setShowPasswordChange(!showPasswordChange)} className="bg-transparent border-white/10 hover:bg-white/5 text-neutral-300 text-xs h-9" data-testid="toggle-password-change">
                    <Lock className="w-3 h-3 mr-2" /> {showPasswordChange ? t("scan_cancel") : t("settings_pw_change")}
                  </Button>
                </div>
                {showPasswordChange && (
                  <div className="space-y-3 animate-fade-in-up" data-testid="password-change-form">
                    <Input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} placeholder={t("change_pw_current")} className={inputClass} data-testid="pw-current" />
                    <Input type="password" value={pwForm.newPw} onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })} placeholder={t("change_pw_new")} className={inputClass} data-testid="pw-new" />
                    <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} placeholder={t("change_pw_confirm_new")} className={inputClass} data-testid="pw-confirm" />
                    <Button onClick={changePassword} disabled={changingPw} className="w-full bg-pink-600 hover:bg-pink-500 text-white font-bold py-2 border border-pink-400 transition-all duration-300 uppercase tracking-widest text-xs h-10" data-testid="pw-submit">
                      <Lock className="w-3 h-3 mr-2" />{changingPw ? t("change_pw_changing") : t("change_pw_confirm_btn")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── Audit Log Tab ─────────────────────────── */}
          <TabsContent value="audit" data-testid="audit-tab-content">
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Filters */}
              <div className="flex items-center gap-2 flex-1 flex-wrap">
                <Filter className="w-4 h-4 text-neutral-500 shrink-0" />
                <select
                  value={auditActionFilter}
                  onChange={(e) => { setAuditActionFilter(e.target.value); setAuditPage(1); }}
                  className="bg-black/50 border border-white/10 text-neutral-300 text-xs rounded-lg h-9 px-3 focus:border-purple-500/50 outline-none"
                  data-testid="audit-action-filter"
                >
                  <option value="">{t("audit_all")}</option>
                  {auditActions.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-500" />
                  <input
                    value={auditActorFilter}
                    onChange={(e) => { setAuditActorFilter(e.target.value); setAuditPage(1); }}
                    placeholder={t("audit_filter_actor")}
                    className="bg-black/50 border border-white/10 text-neutral-300 text-xs rounded-lg h-9 pl-8 pr-3 w-40 focus:border-purple-500/50 outline-none placeholder:text-neutral-600"
                    data-testid="audit-actor-filter"
                  />
                </div>
                {(auditActionFilter || auditActorFilter) && (
                  <button onClick={() => { setAuditActionFilter(""); setAuditActorFilter(""); setAuditPage(1); }} className="text-xs text-purple-400 hover:text-purple-300 underline" data-testid="audit-clear-filters">{t("audit_clear")}</button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">{filteredAudit.length} {t("audit_entries")}</span>
                <Button
                  onClick={() => { fetchAuditLogs(); fetchStats(); }}
                  variant="outline"
                  className="bg-transparent border-white/10 hover:bg-white/5 text-neutral-300 text-xs h-9 gap-2"
                  data-testid="refresh-audit-btn"
                >
                  <History className="w-3 h-3" /> {t("audit_refresh")}
                </Button>
              </div>
            </div>
            <div className="glass-card rounded-xl overflow-x-auto">
              {filteredAudit.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">{auditActionFilter || auditActorFilter ? t("audit_no_match") : t("audit_no_logs")}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-neutral-800 hover:bg-transparent">
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500 w-44">{t("audit_timestamp")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500 w-40">{t("audit_action")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500 w-32">{t("audit_actor")}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-neutral-500">{t("audit_details")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAudit.map((log, idx) => {
                      const actionColors = {
                        LOGIN: "text-emerald-400 bg-emerald-500/10",
                        LOGIN_FAILED: "text-red-400 bg-red-500/10",
                        CLIENT_CREATED: "text-blue-400 bg-blue-500/10",
                        CLIENT_DELETED: "text-red-400 bg-red-500/10",
                        DISCOUNT_CLAIMED: "text-pink-400 bg-pink-500/10",
                        REFERRAL_ADDED: "text-purple-400 bg-purple-500/10",
                        CERTIFICATE_EMAILED: "text-cyan-400 bg-cyan-500/10",
                        GALLERY_UPLOAD: "text-amber-400 bg-amber-500/10",
                        GALLERY_DELETED: "text-orange-400 bg-orange-500/10",
                        SETTINGS_UPDATED: "text-violet-400 bg-violet-500/10",
                        PASSWORD_CHANGED: "text-yellow-400 bg-yellow-500/10",
                        REVIEW_DELETED: "text-rose-400 bg-rose-500/10",
                        CONTACT_SUBMITTED: "text-teal-400 bg-teal-500/10",
                        DATA_EXPORTED: "text-emerald-400 bg-emerald-500/10",
                      };
                      const color = actionColors[log.action] || "text-neutral-400 bg-neutral-500/10";
                      return (
                        <TableRow key={log.id || idx} className="border-b border-neutral-900 hover:bg-white/5" data-testid={`audit-row-${idx}`}>
                          <TableCell className="text-neutral-500 text-xs font-mono">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${color}`}>
                              {log.action}
                            </span>
                          </TableCell>
                          <TableCell className="text-neutral-300 text-sm">{log.actor_name}</TableCell>
                          <TableCell className="text-neutral-400 text-sm">{log.details}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
            {/* Pagination */}
            {totalAuditPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-2" data-testid="audit-pagination">
                <span className="text-xs text-neutral-500">Page {auditPage} of {totalAuditPages}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setAuditPage((p) => Math.max(1, p - 1))} disabled={auditPage === 1} className="p-2 rounded bg-white/5 hover:bg-white/10 text-neutral-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="audit-prev"><ChevronLeft className="w-4 h-4" /></button>
                  {Array.from({ length: Math.min(5, totalAuditPages) }, (_, i) => {
                    let page;
                    if (totalAuditPages <= 5) page = i + 1;
                    else if (auditPage <= 3) page = i + 1;
                    else if (auditPage >= totalAuditPages - 2) page = totalAuditPages - 4 + i;
                    else page = auditPage - 2 + i;
                    return (
                      <button key={page} onClick={() => setAuditPage(page)} className={`w-8 h-8 rounded text-xs font-bold transition-colors ${auditPage === page ? "bg-purple-600 text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"}`} data-testid={`audit-page-${page}`}>{page}</button>
                    );
                  })}
                  <button onClick={() => setAuditPage((p) => Math.min(totalAuditPages, p + 1))} disabled={auditPage === totalAuditPages} className="p-2 rounded bg-white/5 hover:bg-white/10 text-neutral-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="audit-next"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── History Dialog ──────────────────────────── */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-purple-500/20 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {historyData?.client?.name ? t("history_title", { name: historyData.client.name }) : t("history_default")}
            </DialogTitle>
            <DialogDescription className="text-neutral-500">
              {t("history_subtitle")}
            </DialogDescription>
          </DialogHeader>

          {historyLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!historyLoading && historyData && (
            <div className="space-y-6 mt-4">
              {/* Client summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">{t("history_current_disc")}</p>
                  <p className="text-2xl font-bold text-purple-400">{historyData.client.discount}%</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">{t("history_total_ref")}</p>
                  <p className="text-2xl font-bold text-pink-400">{historyData.client.referral_count}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">{t("history_claims_made")}</p>
                  <p className="text-2xl font-bold text-neutral-300">{historyData.discount_claims.length}</p>
                </div>
              </div>

              {/* Discount Claims */}
              <div>
                <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3">{t("history_disc_claims")}</h4>
                {historyData.discount_claims.length === 0 ? (
                  <p className="text-sm text-neutral-500">{t("history_no_claims")}</p>
                ) : (
                  <div className="space-y-2">
                    {historyData.discount_claims.map((claim, idx) => (
                      <div key={claim.id || idx} className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-lg" data-testid={`claim-history-${idx}`}>
                        <div>
                          <p className="text-sm text-neutral-300">
                            <span className="text-pink-400 font-bold">{claim.discount_claimed}%</span> {t("history_pct_claimed", { pct: claim.discount_claimed }).split(`${claim.discount_claimed}%`).pop()}
                          </p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {t("history_refs_at_time", { count: claim.referral_count_at_time })}
                            {claim.notes && ` — ${claim.notes}`}
                          </p>
                        </div>
                        <span className="text-xs text-neutral-500 shrink-0">{new Date(claim.claimed_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Referrals */}
              <div>
                <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3">{t("history_ref_activity")}</h4>
                {historyData.referrals.length === 0 ? (
                  <p className="text-sm text-neutral-500">{t("history_no_refs")}</p>
                ) : (
                  <div className="space-y-2">
                    {historyData.referrals.map((ref, idx) => (
                      <div key={ref.id || idx} className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-lg" data-testid={`referral-history-${idx}`}>
                        <p className="text-sm text-neutral-300">
                          {t("history_referred")} <span className="text-purple-300 font-semibold">{ref.new_client_name}</span>
                        </p>
                        <span className="text-xs text-neutral-500">{new Date(ref.date_used).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation Dialog 1 ──────────── */}
      <Dialog open={deleteStep === 1} onOpenChange={(open) => { if (!open) cancelDelete(); }}>
        <DialogContent className="bg-[#0a0a0a] border border-red-500/30 text-white max-w-md" data-testid="delete-confirm-1">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> {t("delete_title")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 mt-2">
              {t("delete_desc", { name: deleteTarget?.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={cancelDelete} variant="outline" className="flex-1 bg-transparent border-white/10 hover:bg-white/5 text-neutral-300" data-testid="delete-cancel-1">{t("delete_cancel")}</Button>
            <Button onClick={proceedDelete} className="flex-1 bg-red-600 hover:bg-red-500 text-white border border-red-400" data-testid="delete-proceed-1">{t("delete_proceed")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation Dialog 2 (Final) ─────── */}
      <Dialog open={deleteStep === 2} onOpenChange={(open) => { if (!open) cancelDelete(); }}>
        <DialogContent className="bg-[#0a0a0a] border border-red-500/50 text-white max-w-md" data-testid="delete-confirm-2">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> {t("delete_final")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 mt-2">
              {t("delete_permanent")} {t("delete_permanent_text", { name: deleteTarget?.name, info: deleteTarget?.username || deleteTarget?.email })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={cancelDelete} variant="outline" className="flex-1 bg-transparent border-white/10 hover:bg-white/5 text-neutral-300" data-testid="delete-cancel-2">{t("delete_cancel")}</Button>
            <Button onClick={proceedDelete} className="flex-1 bg-red-700 hover:bg-red-600 text-white border border-red-500 font-bold uppercase tracking-wider" data-testid="delete-confirm-final">{t("delete_confirm")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Reassign Client Dialog ──────────────── */}
      <Dialog open={!!reassignTarget} onOpenChange={(open) => { if (!open) { setReassignTarget(null); setReassignArtistId(""); } }}>
        <DialogContent className="bg-[#0a0a0a] border border-cyan-500/30 text-white max-w-md" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-cyan-400" /> {t("reassign_title")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 mt-2">
              {t("reassign_desc", { name: reassignTarget?.name })} {t("reassign_current")}: <strong className="text-pink-300">{reassignTarget?.assigned_artist_name || t("unassigned")}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <select
              value={reassignArtistId}
              onChange={(e) => setReassignArtistId(e.target.value)}
              className="w-full bg-black/50 border border-white/10 text-neutral-300 text-sm rounded-lg h-12 px-3 focus:border-cyan-500/50 outline-none"
              data-testid="reassign-artist-select"
            >
              <option value="">{t("reassign_select")}</option>
              <option value={authUser?.id}>{t("reassign_master")} ({authUser?.name})</option>
              {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <div className="flex gap-3">
              <Button onClick={() => { setReassignTarget(null); setReassignArtistId(""); }} variant="outline" className="flex-1 bg-transparent border-white/10 hover:bg-white/5 text-neutral-300" data-testid="reassign-cancel">{t("delete_cancel")}</Button>
              <Button onClick={reassignClient} disabled={!reassignArtistId} className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white border border-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="reassign-confirm">{t("reassign_confirm")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Reset Artist Password Dialog ───────── */}
      <Dialog open={!!resetPwTarget} onOpenChange={(open) => { if (!open) { setResetPwTarget(null); setResetPwValue(""); } }}>
        <DialogContent className="bg-[#0a0a0a] border border-amber-500/30 text-white max-w-md" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-400" /> {t("reset_pw_title")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 mt-2">
              {t("reset_pw_desc", { name: resetPwTarget?.name })} ({resetPwTarget?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <Input
              type="text"
              value={resetPwValue}
              onChange={(e) => setResetPwValue(e.target.value)}
              placeholder={t("reset_pw_placeholder")}
              className={inputClass}
              data-testid="reset-pw-input"
            />
            <div className="flex gap-3">
              <Button onClick={() => { setResetPwTarget(null); setResetPwValue(""); }} variant="outline" className="flex-1 bg-transparent border-white/10 hover:bg-white/5 text-neutral-300" data-testid="reset-pw-cancel">{t("delete_cancel")}</Button>
              <Button onClick={resetArtistPassword} disabled={!resetPwValue || resetPwValue.length < 6} className="flex-1 bg-amber-600 hover:bg-amber-500 text-white border border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="reset-pw-confirm">{t("reset_pw_confirm")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
